import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSitePages, type SitePageRow } from "@/hooks/useSitePages";

export type MenuKey =
  | "header"
  | "footer"
  | "dashboard"
  | "mobile_bar"
  | "student_dashboard"
  | "guest_dashboard";
export type LinkType = "route" | "external" | "group" | "page";

export type MenuItemRow = {
  id: string;
  menu_key: MenuKey;
  parent_id: string | null;
  label_en: string;
  label_es: string;
  link_type: LinkType;
  link_value: string | null;
  icon_key: string | null;
  is_active: boolean;
  sort_order: number;
};

export type MenuItemNode = MenuItemRow & { children: MenuItemNode[] };

export function buildTree(rows: MenuItemRow[]): MenuItemNode[] {
  const byId = new Map<string, MenuItemNode>();
  rows.forEach((r) => byId.set(r.id, { ...r, children: [] }));
  const roots: MenuItemNode[] = [];
  rows.forEach((r) => {
    const node = byId.get(r.id)!;
    if (r.parent_id && byId.has(r.parent_id)) {
      byId.get(r.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (list: MenuItemNode[]) => {
    list.sort((a, b) => a.sort_order - b.sort_order);
    list.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

/** True when the current viewer may see a link pointing at `url`. */
function canSeeUrl(url: string | null, pages: SitePageRow[], signedIn: boolean, roles: string[]) {
  if (!url) return true;
  const page = pages.find((p) => p.url === url);
  if (!page) return true;
  if (!page.is_active) return false;
  const visibility = page.visibility || "public";
  if (visibility === "public") return true;
  if (!signedIn) return false;
  if (visibility === "authenticated") return true;
  const allowed = page.allowed_roles || [];
  if (allowed.length === 0) return true;
  return roles.some((r) => allowed.includes(r));
}

function filterTree(
  nodes: MenuItemNode[],
  pages: SitePageRow[],
  signedIn: boolean,
  roles: string[],
): MenuItemNode[] {
  return nodes.reduce<MenuItemNode[]>((acc, node) => {
    const children = filterTree(node.children, pages, signedIn, roles);
    const isGroup = node.link_type === "group";
    if (isGroup) {
      if (children.length > 0) acc.push({ ...node, children });
      return acc;
    }
    if (canSeeUrl(node.link_value, pages, signedIn, roles)) acc.push({ ...node, children });
    return acc;
  }, []);
}

export function useMenu(menuKey: MenuKey) {
  const { user, userRoles } = useAuth();
  const { data: pages } = useSitePages();

  const query = useQuery({
    queryKey: ["menu", menuKey],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase.from as any)("menu_items")
        .select("*")
        .eq("menu_key", menuKey)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return buildTree((data ?? []) as MenuItemRow[]);
    },
  });

  const filtered = useMemo(
    () => filterTree(query.data || [], pages || [], !!user, userRoles || []),
    [query.data, pages, user, userRoles],
  );

  return { ...query, data: filtered };
}

// Admin-mode: return all items including inactive.
export function useMenuAdmin(menuKey: MenuKey) {

  return useQuery({
    queryKey: ["menu-admin", menuKey],
    queryFn: async () => {
      const { data, error } = await (supabase.from as any)("menu_items")
        .select("*")
        .eq("menu_key", menuKey)
        .order("sort_order");
      if (error) throw error;
      return {
        rows: (data ?? []) as MenuItemRow[],
        tree: buildTree((data ?? []) as MenuItemRow[]),
      };
    },
  });
}
